import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const MAX_WAIT_MS = 3000;
const RETRY_MS = 100;
const ANCHOR_NAME = /^[A-Za-z0-9_-]+$/;

/**
 * Tiefenlink `?scrollTo=NAME`: springt nach dem Laden EINMAL je Verlaufseintrag zum ersten
 * Element mit `data-test-anchor="NAME"`. Für Seiten, die ihre Abschnitte asynchron laden —
 * der Anker wird bis zu drei Sekunden lang gesucht.
 *
 * `ready` = die Daten der Seite sind da. Der Parameter bleibt in der Adresse stehen;
 * `useScrollRestore` lässt ihm den Vorrang.
 */
export function useAnchorScroll(ready: boolean): void {
  const location = useLocation();
  const name = new URLSearchParams(location.search).get('scrollTo');
  const key = location.key;
  const doneFor = useRef<string | null>(null);

  useEffect(() => {
    if (!ready || !name || !ANCHOR_NAME.test(name)) return;
    const token = `${key}|${name}`;
    if (doneFor.current === token) return;
    const started = Date.now();
    let timer = 0;
    const attempt = () => {
      const el = document.querySelector(`[data-test-anchor="${name}"]`);
      if (el) {
        doneFor.current = token;
        el.scrollIntoView({ block: 'start' });
        return;
      }
      if (Date.now() - started > MAX_WAIT_MS) return;
      timer = window.setTimeout(attempt, RETRY_MS);
    };
    timer = window.setTimeout(attempt, RETRY_MS);
    return () => window.clearTimeout(timer);
  }, [ready, name, key]);
}
